import { useEffect, useMemo, useState } from 'react';
import { useCompleteLesson, useCourse, useEnrol, useSubmitQuiz } from '../../api/hooks';
import type { Lesson, LearningCourse, QuizResult } from '../../api/types';
import { Icon, type IconName } from '../../ui/Icon';
import { sanitizeHtml } from '../../ui/RichTextEditor';
import { useT } from '../../i18n/LangProvider';
import { Empty, LessonBadge, Ring, courseMinutes, fileUrl, fmtDue, lessonMeta } from './atoms';

// ── per-type players ───────────────────────────────────────────────────────

function ArticleView({ lesson }: { lesson: Lesson }) {
  return (
    <div className="lrn-article md-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(lesson.body ?? '') }} />
  );
}

/** YouTube and Vimeo need an iframe embed; a <video> tag cannot play them. */
function embedUrl(src: string): string | null {
  const yt = src.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = src.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
}

function VideoView({ lesson, onEnded }: { lesson: Lesson; onEnded: () => void }) {
  const t = useT();
  if (!lesson.src?.trim()) return <div className="lrn-article muted">{t('Nessun video caricato.')}</div>;
  const embed = embedUrl(lesson.src.trim());
  if (embed) {
    // 16:9 box: the iframe fills it, so the layout no longer collapses.
    return (
      <div className="lrn-video lrn-video--embed">
        <iframe
          src={embed}
          title={lesson.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <div className="lrn-video">
      <video src={fileUrl(lesson.src)} controls playsInline onEnded={onEnded} />
    </div>
  );
}

function DocView({ lesson }: { lesson: Lesson }) {
  const t = useT();
  if (!lesson.src) return <div className="lrn-article muted">{t('Nessun documento caricato.')}</div>;
  return (
    <div className="lrn-doc__frame">
      <iframe src={fileUrl(lesson.src)} title={lesson.title} style={{ width: '100%', height: 520, border: 0, display: 'block' }} />
    </div>
  );
}

function LinkView({ lesson }: { lesson: Lesson }) {
  const t = useT();
  return (
    <div className="lrn-article">
      <p>{t('Questa lezione si svolge su una risorsa esterna.')}</p>
      <a className="btn btn--brand" href={lesson.href} target="_blank" rel="noreferrer">
        <Icon name="external" size={14} /> {t('Apri la risorsa')}
      </a>
      <p className="muted" style={{ fontSize: 12.5, marginTop: 10, wordBreak: 'break-all' }}>{lesson.href}</p>
    </div>
  );
}

function QuizView({ courseId, lesson, alreadyPassed, onGraded }: {
  courseId: string; lesson: Lesson; alreadyPassed: boolean; onGraded: (r: QuizResult) => void;
}) {
  const t = useT();
  const submit = useSubmitQuiz();
  const questions = lesson.questions ?? [];
  const [picked, setPicked] = useState<number[]>(() => questions.map(() => -1));
  const [result, setResult] = useState<QuizResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A different lesson reuses this component: start over.
  useEffect(() => { setPicked(questions.map(() => -1)); setResult(null); setError(null); }, [lesson.id]);

  const allAnswered = picked.every((p) => p >= 0);

  async function send() {
    setError(null);
    try {
      const r = await submit.mutateAsync({ courseId, lessonId: lesson.id, answers: picked });
      setResult(r);
      onGraded(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Invio non riuscito.'));
    }
  }

  if (questions.length === 0) return <div className="lrn-article muted">{t('Questo quiz non ha ancora domande.')}</div>;

  return (
    <div className="lrn-quiz">
      {result && (
        <div className={`lrn-quiz__result ${result.pass ? 'is-ok' : 'is-fail'}`}>
          <Icon name={result.pass ? 'check' : 'x'} size={15} />
          {result.pass
            ? t('Superato con {score}% (minimo {min}%).', { score: result.score, min: result.passScore })
            : t('Non superato: {score}%, serve almeno {min}%. Puoi riprovare.', { score: result.score, min: result.passScore })}
        </div>
      )}
      {alreadyPassed && !result && (
        <div className="lrn-quiz__result is-ok"><Icon name="check" size={15} /> {t('Quiz già superato.')}</div>
      )}

      {questions.map((q, qi) => (
        <div key={qi} className="lrn-quiz__q">
          <div className="lrn-quiz__title">{qi + 1}. {q.q}</div>
          {q.image && <img className="lrn-quiz__img" src={fileUrl(q.image)} alt="" />}
          {q.options.map((opt, oi) => {
            // After grading we can only flag the question, never reveal the key.
            const graded = result ? (result.correct[qi] ? 'is-correct' : 'is-wrong') : '';
            const cls = picked[qi] === oi ? `is-picked ${graded}` : '';
            return (
              <label key={oi} className={`lrn-quiz__opt ${cls}`}>
                <input
                  type="radio"
                  name={`q-${lesson.id}-${qi}`}
                  checked={picked[qi] === oi}
                  disabled={!!result && result.pass}
                  onChange={() => setPicked((p) => p.map((v, i) => (i === qi ? oi : v)))}
                />
                {opt}
              </label>
            );
          })}
        </div>
      ))}

      {error && <div className="lrn-quiz__result is-fail"><Icon name="warn" size={15} /> {error}</div>}

      <div className="cluster" style={{ justifyContent: 'space-between' }}>
        <span className="muted" style={{ fontSize: 12.5 }}>
          {allAnswered ? t('{n} risposte pronte', { n: questions.length }) : t('Rispondi a tutte le domande per inviare.')}
        </span>
        <span className="cluster">
          {result && !result.pass && (
            <button className="btn btn--sm" onClick={() => { setResult(null); setPicked(questions.map(() => -1)); }}>
              {t('Riprova')}
            </button>
          )}
          <button className="btn btn--sm btn--brand" disabled={!allAnswered || submit.isPending || (!!result && result.pass)} onClick={() => void send()}>
            {submit.isPending ? t('Invio…') : t('Invia risposte')}
          </button>
        </span>
      </div>
    </div>
  );
}

// ── course player ──────────────────────────────────────────────────────────

export function CoursePlayer({ courseId, onBack, onToast }: {
  courseId: string; onBack: () => void; onToast: (m: string) => void;
}) {
  const t = useT();
  const courseQ = useCourse(courseId);
  const complete = useCompleteLesson();
  const enrol = useEnrol();
  const [lessonId, setLessonId] = useState<string | null>(null);

  const course = courseQ.data as LearningCourse | undefined;
  const lessons = useMemo(() => course?.lessons ?? [], [course]);
  const assignment = course?.myAssignment ?? null;
  const done = useMemo(() => new Set(assignment?.completedLessons ?? []), [assignment]);

  // Resume where the learner left off, else the first unfinished lesson.
  useEffect(() => {
    if (lessonId || lessons.length === 0) return;
    const resume = assignment?.lastLessonId && lessons.some((l) => l.id === assignment.lastLessonId)
      ? assignment.lastLessonId
      : (lessons.find((l) => !done.has(l.id)) ?? lessons[0]).id;
    setLessonId(resume);
  }, [lessons, assignment, lessonId, done]);

  if (courseQ.isLoading) return <div className="lrn-empty">{t('Carico il corso…')}</div>;
  if (!course) return <Empty title={t('Corso non trovato')} action={<button className="btn" onClick={onBack}>{t('Torna indietro')}</button>} />;

  const lesson = lessons.find((l) => l.id === lessonId) ?? lessons[0];
  const index = lessons.findIndex((l) => l.id === lesson?.id);
  const progress = assignment?.progress ?? 0;
  const completed = assignment?.status === 'completed';

  async function markDone(next: boolean) {
    if (!lesson) return;
    try {
      await complete.mutateAsync({ courseId, lessonId: lesson.id, done: next });
      if (next) {
        const nxt = lessons[index + 1];
        if (nxt) setLessonId(nxt.id);
        else onToast(t('Corso completato!'));
      }
    } catch (e) {
      onToast(e instanceof Error ? e.message : t('Operazione non riuscita'));
    }
  }

  return (
    <div className="lrn">
      <div className="lrn-subbar">
        <button className="lrn-back" onClick={onBack}><Icon name="chevL" size={13} /> {t('Formazione')}</button>
        <div className="lrn-subbar__title"><Icon name={(course.icon || 'cap') as IconName} size={16} /> {course.title}</div>
        <div className="cluster">
          {completed && <span className="lrn-quiz__result is-ok" style={{ padding: '6px 12px' }}><Icon name="award" size={14} /> {t('Completato')}</span>}
          <Ring value={progress} size={40} />
        </div>
      </div>

      {!assignment && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div className="spaced">
            <div>
              <strong>{t('Non sei ancora iscritto a questo corso.')}</strong>
              <div className="muted" style={{ fontSize: 13 }}>{t('Iscriviti per registrare i tuoi progressi.')}</div>
            </div>
            <button className="btn btn--brand" disabled={enrol.isPending} onClick={() => enrol.mutate(courseId)}>
              <Icon name="plus" size={14} /> {t('Iscrivimi')}
            </button>
          </div>
        </div>
      )}

      <div className="lrn-detail__body">
        <div className="lrn-lessons">
          <div className="muted" style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>
            {t('{n} lezioni', { n: lessons.length })} · {courseMinutes(lessons)} min
          </div>
          {lessons.map((l, i) => {
            const isDone = done.has(l.id);
            return (
              <button key={l.id} className={`lrn-lesson ${l.id === lesson?.id ? 'is-active' : ''}`} onClick={() => setLessonId(l.id)}>
                <span className={`lrn-lesson__mark ${isDone ? 'is-done' : ''}`}>
                  {isDone ? <Icon name="check" size={11} stroke={3} /> : i + 1}
                </span>
                <span className="lrn-lesson__body">
                  <span className="lrn-lesson__title">{l.title}</span>
                  <span className="lrn-lesson__meta">
                    <Icon name={lessonMeta(l.type).icon} size={11} /> {t(lessonMeta(l.type).label)}
                    {l.durationMin ? ` · ${l.durationMin} min` : ''}
                  </span>
                </span>
              </button>
            );
          })}
          {assignment?.dueAt && (
            <div className="muted" style={{ fontSize: 12.5, marginTop: 6 }}>
              <Icon name="clock" size={12} /> {t('Scadenza:')} {fmtDue(assignment.dueAt)}
            </div>
          )}
        </div>

        <div className="card lrn-player">
          {!lesson ? (
            <Empty title={t('Nessuna lezione')} hint={t('Questo corso non ha ancora contenuti.')} />
          ) : (
            <>
              <div className="lrn-player__head">
                <div>
                  <div className="lrn-player__kind"><LessonBadge type={lesson.type} /></div>
                  <div className="lrn-player__title">{lesson.title}</div>
                </div>
                <div className="cluster">
                  <button className="btn btn--sm" disabled={index <= 0} onClick={() => setLessonId(lessons[index - 1].id)}>
                    <Icon name="chevL" size={12} /> {t('Prec.')}
                  </button>
                  <button className="btn btn--sm" disabled={index >= lessons.length - 1} onClick={() => setLessonId(lessons[index + 1].id)}>
                    {t('Succ.')} <Icon name="chevR" size={12} />
                  </button>
                </div>
              </div>

              <div className="lrn-player-body">
                {lesson.type === 'article' && <ArticleView lesson={lesson} />}
                {lesson.type === 'video' && <VideoView lesson={lesson} onEnded={() => void markDone(true)} />}
                {lesson.type === 'document' && <DocView lesson={lesson} />}
                {lesson.type === 'link' && <LinkView lesson={lesson} />}
                {lesson.type === 'quiz' && (
                  <QuizView courseId={courseId} lesson={lesson} alreadyPassed={done.has(lesson.id)}
                    onGraded={(r) => onToast(r.pass ? t('Quiz superato ({score}%)', { score: r.score }) : t('Quiz non superato ({score}%)', { score: r.score }))} />
                )}
              </div>

              {lesson.type !== 'quiz' && (
                <div className="lrn-modal__foot">
                  <span className="muted" style={{ fontSize: 12.5 }}>
                    {t('Lezione {i} di {n}', { i: index + 1, n: lessons.length })}
                  </span>
                  {done.has(lesson.id) ? (
                    <button className="btn btn--sm" disabled={complete.isPending} onClick={() => void markDone(false)}>
                      <Icon name="x" size={12} /> {t('Segna come da fare')}
                    </button>
                  ) : (
                    <button className="btn btn--sm btn--brand" disabled={complete.isPending} onClick={() => void markDone(true)}>
                      <Icon name="check" size={12} /> {t('Segna come completata')}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
